/* eslint-disable @typescript-eslint/ban-ts-comment */
import { AutomaticEntityValidator, hasValidation, IEntityValidationRules, IHasValidation } from "@frui.ts/validation";
import { PropertyName } from "@frui.ts/helpers";
import { get, observable } from "mobx";
import { ValidationControlBase } from "@frui.ts/bootstrap";
import TaggedInput from "@edaz/edazkit/TaggedInput";
import EdazPhoneInput from "@edaz/edazkit/InputPhone";

export interface IHasBlurValidation<TTarget> {
  // @ts-ignore
  __validation: BlurAutomaticEntityValidator<TTarget>;
}

export default class BlurAutomaticEntityValidator<TTarget extends Record<string, any>> extends AutomaticEntityValidator<TTarget> {
  @observable visitedProperties: string[] = [];
}

export function hasBlurValidation<TTarget>(target: any): target is IHasBlurValidation<TTarget> {
  return (
    (target as IHasValidation<TTarget>)?.__validation !== undefined &&
    (target as IHasValidation<TTarget>).__validation instanceof BlurAutomaticEntityValidator
  );
}

export function getValidationMessage<TTarget>(target: TTarget, propertyName: PropertyName<TTarget>): string | undefined {
  if (
    (hasBlurValidation<TTarget>(target) &&
      (target.__validation.isErrorsVisible || target.__validation.visitedProperties.includes(propertyName))) ||
    (hasValidation<TTarget>(target) && target.__validation.isErrorsVisible)
  ) {
    return get(target.__validation.errors, propertyName);
  }

  return undefined;
}

export function setVisitedProperty<TTarget>(target: TTarget, propertyName: PropertyName<TTarget>) {
  if (hasBlurValidation<TTarget>(target)) {
    target.__validation.visitedProperties.push(propertyName);
  }
}

export function attachAutomaticBlurValidator<TTarget>(
  target: TTarget,
  entityValidationRules: IEntityValidationRules<TTarget>,
  errorsImmediatelyVisible = false
) {
  const typedTarget = target as TTarget & IHasValidation<TTarget>;
  // @ts-ignore
  typedTarget.__validation = new BlurAutomaticEntityValidator(target, entityValidationRules, errorsImmediatelyVisible);
  return typedTarget;
}

/**
 * This monkeyPatch edits ValidationControlBase component to use ours "getValidationMessage" function instead of Frui.ts prebuilded one.
 * It deal with validation errors visibility after onBlur action directly.
 *
 * https://github.com/eManPrague/frui.ts/issues/34
 */
export function monkeyPatchValidationControlBase() {
  /* eslint-disable @typescript-eslint/ban-ts-comment */

  // @ts-ignore
  Object.defineProperty(ValidationControlBase.prototype, "inheritedProps", {
    get: function () {
      const { target, property, onValueChanged, noValidation, errorMessage, onBlur, ...otherProps } = this.props;
      const modifiedOnBlur = () => {
        if (onBlur) {
          onBlur();
        }
        setVisitedProperty(target, property);
      };

      return {
        onBlur: modifiedOnBlur,
        ...otherProps,
      };
    },
  });

  // @ts-ignore
  ValidationControlBase.prototype.getValidationError =
    // @ts-ignore
    EdazPhoneInput.prototype.__proto__.getValidationError =
    // @ts-ignore
    TaggedInput.prototype.__proto__.getValidationError =
      function () {
        const { noValidation, errorMessage } = this.props;

        if (noValidation === true) {
          return undefined;
        }

        if (errorMessage) {
          return errorMessage;
        }

        const target = this.props.target;
        const property = this.props.property as any;

        if (target && property) {
          return getValidationMessage(target, property);
        }

        return undefined;
      };

  /* eslint-enable @typescript-eslint/ban-ts-comment */
}
